import { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PaymentModal from "../components/PaymentModal";

export default function Checkout() {
  const { cart, getCartTotal, loading, fetchCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [address, setAddress] = useState({
    fullName: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [submitting, setSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  if (loading) {
    return <h1 className="text-center mt-10 text-xl">Loading cart...</h1>;
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="text-center mt-10">
        <h1 className="text-2xl font-bold mb-4">Your cart is empty!</h1>
        <button
          onClick={() => navigate("/products")}
          className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  const handlePlaceOrder = async () => {
    if (
      !address.fullName ||
      !address.phone ||
      !address.street ||
      !address.city ||
      !address.state ||
      !address.pincode
    ) {
      toast.error("Please fill all address fields!");
      return;
    }

    setSubmitting(true);

    const orderData = {
      items: cart.items.map((item) => ({
        product: item.product._id,
        quantity: item.quantity,
        price: item.price,
      })),
      totalAmount: getCartTotal(),
      shippingAddress: address,
      paymentMethod: paymentMethod,
    };

    const res = await api.createOrder(orderData);

    setSubmitting(false);

    if (res.success) {
      const orderId = res.data.order._id;
      setCreatedOrderId(orderId);

      await fetchCart();

      if (paymentMethod === "COD") {
        toast.success("Order placed successfully! 🎉");
        navigate("/orders");
      } else {
        setShowPaymentModal(true);
      }
    } else {
      toast.error(res.message || "Failed to place order");
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    await api.updatePayment(createdOrderId, {
      paymentStatus: "Completed",
      paymentId: paymentData.transactionId,
    });

    setShowPaymentModal(false);
    toast.success("Payment successful! Order placed! 🎉");

    await fetchCart();
    navigate("/orders");
  };

  return (
    <div className="max-w-6xl mx-auto mt-10 p-6 grid grid-cols-1 md:grid-cols-2 gap-10">
      {/* SHIPPING ADDRESS */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Shipping Address</h2>

        <div className="space-y-3">
          <input
            type="text"
            placeholder="Full Name"
            value={address.fullName}
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={(e) =>
              setAddress({ ...address, fullName: e.target.value })
            }
          />

          <input
            type="tel"
            placeholder="Phone Number"
            value={address.phone}
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={(e) =>
              setAddress({ ...address, phone: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="Street Address"
            value={address.street}
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={(e) =>
              setAddress({ ...address, street: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="City"
            value={address.city}
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={(e) =>
              setAddress({ ...address, city: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="State"
            value={address.state}
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={(e) =>
              setAddress({ ...address, state: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="Pincode"
            value={address.pincode}
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={(e) =>
              setAddress({ ...address, pincode: e.target.value })
            }
          />
        </div>

        {/* PAYMENT METHOD SECTION */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4">Payment Method</h2>

          <div className="space-y-3">
            {/* Cash on Delivery */}
            <label
              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                paymentMethod === "COD"
                  ? "border-purple-600 bg-purple-50"
                  : "border-gray-200 hover:border-purple-300"
              }`}
            >
              <input
                type="radio"
                name="paymentMethod"
                value="COD"
                checked={paymentMethod === "COD"}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-4 h-4 text-purple-600 focus:ring-purple-500"
              />
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">
                    Cash on Delivery
                  </span>
                  <span className="text-2xl">💵</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Pay when you receive your order
                </p>
              </div>
            </label>

            {/* Online Payment (DISABLED) */}
            <label className="flex items-center p-4 border-2 rounded-lg opacity-50 cursor-not-allowed">
              <input
                type="radio"
                name="paymentMethod"
                value="Online"
                disabled
                className="w-4 h-4 text-purple-600"
              />
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-500">
                    Online Payment
                  </span>
                  <span className="text-2xl">💳</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Currently unavailable
                </p>
              </div>
            </label>

            {/* UPI Payment (DISABLED) */}
            <label className="flex items-center p-4 border-2 rounded-lg opacity-50 cursor-not-allowed">
              <input
                type="radio"
                name="paymentMethod"
                value="UPI"
                disabled
                className="w-4 h-4 text-purple-600"
              />
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-500">
                    UPI Payment
                  </span>
                  <span className="text-2xl">📱</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Currently unavailable
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* ORDER SUMMARY */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Order Summary</h2>

        <div className="space-y-4">
          {cart.items.map((item) => (
            <div
              key={item.product._id}
              className="flex justify-between border-b pb-3"
            >
              <div>
                <p className="font-medium">{item.productSnapshot.name}</p>
                <p className="text-sm text-gray-600">
                  Qty: {item.quantity}
                </p>
              </div>
              <span className="font-semibold">
                ₹{item.price * item.quantity}
              </span>
            </div>
          ))}
        </div>

        <div className="border-t mt-6 pt-4">
          <div className="flex justify-between text-lg mb-2">
            <span>Subtotal</span>
            <span>₹{getCartTotal().toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-lg mb-2">
            <span>Shipping</span>
            <span className="text-green-600">FREE</span>
          </div>

          {paymentMethod === "COD" && (
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>COD Charges</span>
              <span>₹0</span>
            </div>
          )}

          <div className="flex justify-between text-xl font-bold mt-4">
            <span>Total</span>
            <span className="text-purple-600">
              ₹{getCartTotal().toLocaleString()}
            </span>
          </div>
        </div>

        <button
          className="bg-purple-600 text-white w-full py-3 rounded-lg mt-6 hover:bg-purple-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handlePlaceOrder}
          disabled={submitting}
        >
          {submitting
            ? "Placing Order..."
            : `Place Order (${paymentMethod})`}
        </button>

        <button
          className="border border-purple-600 text-purple-600 w-full py-3 rounded-lg mt-3 hover:bg-purple-50 transition font-semibold"
          onClick={() => navigate("/cart")}
        >
          Back to Cart
        </button>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        amount={getCartTotal()}
        orderId={createdOrderId}
        paymentMethod={paymentMethod}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}
